import { getCoverArt, normalizeCoverHash } from '../lib/coverArtIPC';
import { connectorInvoke } from '../lib/connector';

type InvokeFn = (cmd: string, args?: Record<string, unknown>) => Promise<any>;

const LOCAL_ENTITY_HASH_RE = /^[0-9a-f]{16}$/i;

function getInvoke(): InvokeFn | null {
  if (typeof window === 'undefined' || !(window as any).__TAURI__?.core?.invoke) return null;
  return connectorInvoke;
}

/** Strip `local:{entity}:<hash>` URI prefix — identity is the bare 16-char hash. */
export function stripLocalEntityPrefix(
  id?: string | null,
  entityType?: 'album' | 'artist' | 'track',
): string {
  const s = String(id || '').trim();
  if (!s) return '';
  const uriMatch = s.match(/^local:(album|artist|track):([0-9a-f]{16})$/i);
  if (uriMatch) return uriMatch[2].toLowerCase();
  if (entityType && LOCAL_ENTITY_HASH_RE.test(s)) return s.toLowerCase();
  return s;
}

export function normalizeLocalAlbumId(id?: string | null): string {
  return stripLocalEntityPrefix(id, 'album');
}

export function normalizeLocalArtistId(id?: string | null): string {
  return stripLocalEntityPrefix(id, 'artist');
}

export function normalizeLocalTrackId(id?: string | null): string {
  return stripLocalEntityPrefix(id, 'track');
}

/** Build the wire-format stable_id Rust SQLite stores (local:album:<hash>). */
export function toLocalAlbumUri(hash: string): string {
  const bare = normalizeLocalAlbumId(hash);
  return LOCAL_ENTITY_HASH_RE.test(bare) ? `local:album:${bare}` : bare;
}

export function toLocalArtistUri(hash: string): string {
  const bare = normalizeLocalArtistId(hash);
  return LOCAL_ENTITY_HASH_RE.test(bare) ? `local:artist:${bare}` : bare;
}

export function toLocalTrackUri(hash: string): string {
  const bare = normalizeLocalTrackId(hash);
  return LOCAL_ENTITY_HASH_RE.test(bare) ? `local:track:${bare}` : bare;
}

/** Album id for local_library_* IPC — bare hash in UI, prefixed stable_id on the wire. */
export function localAlbumIpcId(id?: string | null): string {
  return toLocalAlbumUri(normalizeLocalAlbumId(id));
}

export function localArtistIpcId(id?: string | null): string {
  return toLocalArtistUri(normalizeLocalArtistId(id));
}

export function localTrackIpcId(id?: string | null): string {
  return toLocalTrackUri(normalizeLocalTrackId(id));
}

export function isLocalEntityHash(id?: string | null): boolean {
  return LOCAL_ENTITY_HASH_RE.test(String(id || ''));
}

export function isLocalLibraryAlbumKey(albumKey?: string | null): boolean {
  const s = String(albumKey || '');
  if (/^local:album:[0-9a-f]{16}$/i.test(s)) return true;
  return LOCAL_ENTITY_HASH_RE.test(s);
}

export function isLocalLibraryArtistKey(artistKey?: string | null): boolean {
  const s = String(artistKey || '');
  if (/^local:artist:[0-9a-f]{16}$/i.test(s)) return true;
  return LOCAL_ENTITY_HASH_RE.test(s);
}

function parseYear(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1000 || n > 2099) return null;
  return Math.trunc(n);
}

async function fileToBase64(file: File): Promise<{ b64: string; mime: string }> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return { b64: btoa(binary), mime: file.type || 'image/jpeg' };
}

export async function loadLocalAlbumMetadata(albumKey: string) {
  const invoke = getInvoke();
  if (!invoke) throw new Error('Local library unavailable');
  const id = localAlbumIpcId(albumKey);
  const resp = await invoke('local_library_get_album', { id });
  if (!resp?.success) {
    throw new Error(typeof resp?.error === 'string' ? resp.error : 'Album not found');
  }
  const a = resp.album || {};
  const trackList = (resp.tracks || []).map((t: any) => ({
    id: t.id,
    title: t.title || '',
    artist_name: t.artist || t.artist_name || '',
    track_num: t.track_number ?? t.track_num ?? null,
    disc_num: t.disc_number ?? t.disc_num ?? null,
    duration: t.duration ?? null,
    format: t.format || '',
    file_path: t.file_path || null,
    custom_metadata: {
      lyrics: t.lyrics || '',
      lyrics_source: t.lyrics_source || '',
    },
  }));

  let coverPreview = a.cover_art_url || '';
  const emb = normalizeCoverHash(a.embedded_cover_hash);
  if (!coverPreview && emb) {
    coverPreview = (await getCoverArt(emb, { albumId: albumKey })) || '';
  }

  return {
    album: {
      title: a.title || '',
      artist_name: a.artist_name || '',
      album_artist: a.album_artist || a.artist_name || '',
      year: a.year ?? null,
      genre: a.genre || '',
      cover_art_url: a.cover_art_url || '',
      format: a.format || '',
      track_count: a.track_count || trackList.length || 0,
      qobuz_match_id: '',
      mb_id: '',
      sample_rate: a.sample_rate ?? null,
      bit_depth: a.bit_depth ?? null,
      embedded_cover_hash: a.embedded_cover_hash || null,
    },
    tracks: trackList,
    metadata: { album: {}, credits: {}, notes: {}, raw_tags: {}, custom: {} },
    coverPreview,
  };
}

export async function patchLocalAlbumMetadata(
  albumKey: string,
  data: {
    title?: string;
    artist_name?: string;
    album_artist?: string;
    year?: unknown;
    genre?: string;
    cover_art_url?: string;
    disc_count?: unknown;
    enrichment_attempted_at?: string;
  },
  options?: { coverFile?: File; coverPath?: string },
): Promise<{
  cover_hash?: string | null;
  fields_updated?: string[];
  new_catalog_version?: number;
}> {
  const invoke = getInvoke();
  if (!invoke) throw new Error('Local library unavailable');

  const coverRaw = String(data.cover_art_url || '').trim();
  const coverUrl = /^https?:\/\//i.test(coverRaw) ? coverRaw : null;
  let coverBytesB64: string | null = null;
  let coverMime: string | null = null;

  if (options?.coverFile) {
    const packed = await fileToBase64(options.coverFile);
    coverBytesB64 = packed.b64;
    coverMime = packed.mime;
  }

  const resp = await invoke('local_library_update_album', {
    id: localAlbumIpcId(albumKey),
    title: data.title?.trim() ? data.title.trim() : null,
    year: parseYear(data.year),
    albumArtist: (data.album_artist || data.artist_name)?.trim()
      ? (data.album_artist || data.artist_name)!.trim()
      : null,
    discCount: data.disc_count != null && data.disc_count !== ''
      ? Number(data.disc_count)
      : null,
    genre: data.genre?.trim() ? data.genre.trim() : null,
    coverUrl,
    coverPath: options?.coverPath || null,
    coverBytesB64,
    coverMime,
    enrichmentAttemptedAt: data.enrichment_attempted_at?.trim()
      ? data.enrichment_attempted_at.trim()
      : null,
  });
  return {
    cover_hash: resp?.cover_hash ?? resp?.coverHash ?? null,
    fields_updated: resp?.fields_updated ?? resp?.fieldsUpdated ?? [],
    new_catalog_version: resp?.new_catalog_version ?? resp?.newCatalogVersion ?? 0,
  };
}

export async function patchLocalArtistMetadata(
  artistKey: string,
  data: {
    name?: string;
    image_url?: string;
    image_source?: string;
    enrichment_attempted_at?: string;
  },
) {
  const invoke = getInvoke();
  if (!invoke) throw new Error('Local library unavailable');
  await invoke('local_library_update_artist', {
    id: localArtistIpcId(artistKey),
    name: data.name?.trim() ? data.name.trim() : null,
    imageUrl: data.image_url?.trim() ? data.image_url.trim() : null,
    imageSource: data.image_source?.trim() ? data.image_source.trim() : null,
    enrichmentAttemptedAt: data.enrichment_attempted_at?.trim()
      ? data.enrichment_attempted_at.trim()
      : null,
  });
}
